<template>
  <base-toggle
    v-model="modelValue"
    :aria-label="accessibilityLabel"
    class="i-flex"
    :class="brandClasses.wrapper"
    data-test-id="vf-switch"
    :disabled
    role="switch"
  >
    <span
      v-if="labelOff"
      aria-hidden="true"
      class="flex items-center"
      :class="[
        brandClasses.label.shared,
        disabled ? brandClasses.label.disabled : brandClasses.label.default,
      ]"
      data-test-id="vf-switch-label-off"
    >
      {{ labelOff }}
    </span>
    <span
      class="w-14 flex items-center rounded-full outline-offset-2 duration forced-colors:outline peer-focus-visible:outline-auto"
      :class="
        modelValue && disabled
          ? ['forced-colors:o:bg-[GrayText]', brandClasses.control.disabledSelected]
          : disabled
            ? ['forced-colors:o:bg-[GrayText]', brandClasses.control.disabled]
            : modelValue
              ? ['forced-colors:o:bg-[SelectedItem]', brandClasses.control.selected]
              : brandClasses.control.default
      "
      data-test-id="vf-switch-track"
      :style="brandStyles.control"
    >
      <span
        class="transform bg-white outline-1 duration forced-colors:outline"
        :class="[brandClasses.handle.default, { [brandClasses.handle.selected]: modelValue }]"
        data-test-id="vf-switch-handle"
      />
    </span>
    <span
      v-if="labelOn"
      aria-hidden="true"
      class="flex items-center"
      :class="[
        brandClasses.label.shared,
        disabled ? brandClasses.label.disabled : brandClasses.label.default,
      ]"
      data-test-id="vf-switch-label-on"
    >
      {{ labelOn }}
    </span>
  </base-toggle>
</template>

<script lang="ts" setup>
const { disabled, labelOff, labelOn } = defineProps<{
  /**
   * Sets the disabled state of the switch
   */
  disabled?: boolean
  /**
   * Label for 'off' position of the switch
   */
  labelOff?: string
  /**
   * Label for 'on' position of the switch
   */
  labelOn?: string
}>()

const modelValue = defineModel<boolean>()

const { $t } = useNuxtApp()
const { brandClasses, brandStyles } = useAppConfig().components.vf.switch

const accessibilityLabel = computed(() => {
  if (labelOff && labelOn) {
    return replaceAll($t.switchAccessibilityLabel, {
      labelFrom: modelValue.value ? labelOn : labelOff,
      labelTo: modelValue.value ? labelOff : labelOn
    })
  }

  if (labelOff || labelOn)
    return replaceAll($t.switchAccessibilityLabelSingleLabel, { label: labelOff || labelOn })

  return $t.switchAccessibilityLabelNoLabels
})
</script>
